import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';

import { SingleElementSelector } from './selection/SingleElementSelector';
import { provideTargetIcon } from './IconProvider';
import {
  isTargetInactiveByState,
  TargetNameAndDescription,
  targetNameAndDescriptionStyle,
} from './TargetNameAndDescription';
import { dbToArray, getTotalVehicleCount, getVehicleCountByFilter, searchTargetsByNameFragment } from '@/components/db';
import { MinimalTarget } from '@/generated/model';

import './SingleTargetSelector.css';

export function SingleTargetSelector({
  setSelectedTarget,
  selectedTarget,
  searchText,
  setSearchText,
  tachographFilter,
}: {
  setSelectedTarget: (target: MinimalTarget | undefined) => void;
  selectedTarget: MinimalTarget | undefined;
  searchText: string | undefined;
  setSearchText: Dispatch<SetStateAction<string | undefined>>;
  tachographFilter?: boolean;
}) {
  const { t } = useTranslation();
  async function fetchData(
    page: number,
    pageSize: number,
    pagesToLoad: number,
    filter: string | undefined,
    order: string
  ): Promise<MinimalTarget[]> {
    const offset = page * pageSize;
    const limit = pageSize * pagesToLoad;

    try {
      let results: MinimalTarget[];

      if (filter) {
        results = await searchTargetsByNameFragment(filter, offset, limit, order, tachographFilter);
      } else {
        results = await dbToArray(offset, limit, order, tachographFilter);
      }

      return results.map((row) => ({
        id: row.id,
        name: row.name,
        description: row.description,
        category: row.category,
        state: row.state,
        comment: row.comment,
        customerName: row.customerName,
      }));
    } catch (error) {
      console.error('Local fetch failed:', error);
      return [];
    }
  }

  async function getRowCount(filter: string | undefined): Promise<number> {
    try {
      if (filter) {
        return (await getVehicleCountByFilter(filter, [], tachographFilter)).count;
      } else {
        return (await getTotalVehicleCount(tachographFilter)).count;
      }
    } catch (error) {
      console.error('Count fetch failed:', error);
      return 0;
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'category',
      headerName: 'Category',
      flex: 0.1,
      renderCell: (params: GridRenderCellParams) => {
        const target = params.row;
        return (
          <div className="target-cell" data-qa-element-id="target-icon">
            <img className="idata-target-icon" src={provideTargetIcon(target.category)} />
          </div>
        );
      },
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const isChecked = selectedTarget ? selectedTarget.id === params.row.id : false;
        const target = params.row;
        return (
          <div className="target-cell">
            <TargetNameAndDescription
              targetName={target.name}
              targetDescription={target.description}
              styleClassName={targetNameAndDescriptionStyle(isChecked, true, isTargetInactiveByState(target))}
            />
          </div>
        );
      },
    },
  ];

  const translation = {
    header: t('general.vehicleSelector'),
    placeHolder: t('filter.targetSearchPlaceholder'),
    noRows: t('filter.noRows'),
    tooManySelected: '',
  };

  return (
    <div className="single-target-selector" data-qa-element-id="single_target_selector_panel">
      <SingleElementSelector
        selectedTarget={selectedTarget}
        setSelectedTarget={setSelectedTarget}
        searchText={searchText}
        setSearchText={setSearchText}
        fetchData={fetchData}
        getRowCount={getRowCount}
        columns={columns}
        translation={translation}
      />
    </div>
  );
}
